// PublicPortfolio — Kullanicinin dis dunyaya acik portfolio sayfasi
// Behance + Linktree + RunAI brand karisimi
// Route: /u/:username (public, auth gerekmez)
// SEO: meta tags, JSON-LD, og:image
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { API_URL, apiFetch } from '../config/api'
import { useAuth } from '../contexts/AuthContext'
import ProfileAICreations, { AICreationItem } from '../components/profile/ProfileAICreations'
import ProfileBuzzPosts from '../components/profile/ProfileBuzzPosts'
import ProfileFollowSystem from '../components/profile/ProfileFollowSystem'
import ProfileGallery, { GalleryItem } from '../components/profile/ProfileGallery'
import ProfileHero, { PortfolioUser } from '../components/profile/ProfileHero'
import ProfileLipSync, { LipSyncItem } from '../components/profile/ProfileLipSync'
import ProfileStats, { ProfileStatsData } from '../components/profile/ProfileStats'
import ProfileTabBar, { PortfolioTab } from '../components/profile/ProfileTabBar'
import ProfileVideos, { VideoItem } from '../components/profile/ProfileVideos'
import ProfileWeb, { WebSiteItem } from '../components/profile/ProfileWeb'

export default function PublicPortfolio() {
  const { username } = useParams<{ username: string }>()
  const { user: currentUser } = useAuth()
  const isOwner = currentUser?.username === username

  const [profile, setProfile] = useState<PortfolioUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<PortfolioTab>('gallery')
  const [theme, setTheme] = useState<'dark' | 'light' | 'runai-purple'>('dark')

  // Tab verileri
  const [gallery, setGallery] = useState<GalleryItem[]>([])
  const [webSites, setWebSites] = useState<WebSiteItem[]>([])
  const [videos, setVideos] = useState<VideoItem[]>([])
  const [lipsyncs, setLipsyncs] = useState<LipSyncItem[]>([])
  const [stats, setStats] = useState<ProfileStatsData | null>(null)
  const [aiCreations, setAiCreations] = useState<AICreationItem[]>([])
  const [tabLoading, setTabLoading] = useState(false)

  // Profil yukle
  useEffect(() => {
    if (!username) return
    setLoading(true)
    apiFetch(`${API_URL}/api/user/profile/${username}`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        if (!d) { setLoading(false); return }
        const u = d.user ?? d
        setProfile({
          id: u.id,
          username: u.username,
          display_name: u.display_name || u.username,
          bio: u.bio,
          avatar_url: u.avatar_url,
          cover_url: u.cover_url,
          cover_color: u.cover_color,
          location: u.location,
          created_at: u.created_at,
          social_accounts: u.social_accounts ?? [],
        })
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [username])

  // Gallery tab — RunImage dosyalari
  const loadGallery = useCallback(() => {
    if (!profile?.id) return
    setTabLoading(true)
    apiFetch(`${API_URL}/api/users/${profile.id}/creations?type=image&limit=50`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then((d: any) => {
        const items: GalleryItem[] = (d.items ?? d.files ?? []).map((f: any) => ({
          id: f.id ?? f.file_id ?? f.url,
          url: f.url ?? f.file_url ?? `${API_URL}/api/files/${f.file_id}/download`,
          title: f.title ?? f.filename ?? f.prompt ?? '',
          type: f.ai_generated ? 'ai_image' : 'photo',
          created_at: f.created_at,
        }))
        setGallery(items)
        setTabLoading(false)
      })
      .catch(() => setTabLoading(false))
  }, [profile?.id])

  // Web tab
  const loadWeb = useCallback(() => {
    if (!profile?.id) return
    setTabLoading(true)
    apiFetch(`${API_URL}/api/users/${profile.id}/creations?type=web&limit=20`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then((d: any) => {
        const items: WebSiteItem[] = (d.items ?? d.sites ?? []).map((s: any) => ({
          id: s.id, title: s.title ?? 'Untitled Site',
          url: s.url ?? s.site_url ?? '#',
          thumbnail_url: s.thumbnail_url, created_at: s.created_at,
        }))
        setWebSites(items)
        setTabLoading(false)
      })
      .catch(() => setTabLoading(false))
  }, [profile?.id])

  // Videos tab
  const loadVideos = useCallback(() => {
    if (!profile?.id) return
    setTabLoading(true)
    apiFetch(`${API_URL}/api/users/${profile.id}/creations?type=video&limit=30`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then((d: any) => {
        const items: VideoItem[] = (d.items ?? d.videos ?? []).map((v: any) => ({
          id: v.id, title: v.title ?? 'Untitled Video',
          url: v.url ?? v.video_url ?? `${API_URL}/api/files/${v.file_id}/download`,
          thumbnail_url: v.thumbnail_url, duration: v.duration,
          created_at: v.created_at,
        }))
        setVideos(items)
        setTabLoading(false)
      })
      .catch(() => setTabLoading(false))
  }, [profile?.id])

  // LipSync tab
  const loadLipSync = useCallback(() => {
    if (!profile?.id) return
    setTabLoading(true)
    apiFetch(`${API_URL}/api/users/${profile.id}/creations?type=lipsync&limit=20`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then((d: any) => {
        const items: LipSyncItem[] = (d.items ?? d.jobs ?? []).map((j: any) => ({
          id: j.id, title: j.title ?? j.text?.slice(0, 40) ?? 'LipSync',
          video_url: j.video_url ?? j.result_url ?? '',
          avatar_name: j.avatar_name, voice_name: j.voice_name,
          thumbnail_url: j.thumbnail_url, created_at: j.created_at,
        }))
        setLipsyncs(items)
        setTabLoading(false)
      })
      .catch(() => setTabLoading(false))
  }, [profile?.id])

  // Stats tab
  const loadStats = useCallback(() => {
    if (!profile?.id) return
    setTabLoading(true)
    apiFetch(`${API_URL}/api/users/${profile.id}/creations?type=stats`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        if (d) {
          setStats({
            total_images: d.total_images ?? gallery.length,
            total_videos: d.total_videos ?? videos.length,
            total_lipsync: d.total_lipsync ?? lipsyncs.length,
            total_web: d.total_web ?? webSites.length,
            flz_earned: d.flz_earned ?? 0,
            level: d.level ?? 1,
            xp: d.xp ?? d.total_xp ?? 0,
            badges: d.badges ?? [],
          })
        }
        setTabLoading(false)
      })
      .catch(() => setTabLoading(false))
  }, [profile?.id, gallery.length, videos.length, lipsyncs.length, webSites.length])

  // AI Creations tab — tum AI uretimler birlestir
  const loadAICreations = useCallback(() => {
    if (!profile?.id) return
    setTabLoading(true)
    apiFetch(`${API_URL}/api/users/${profile.id}/creations?type=all&limit=60`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then((d: any) => {
        const items: AICreationItem[] = (d.items ?? []).map((c: any) => ({
          id: c.id,
          type: c.type ?? 'image',
          title: c.title ?? c.prompt ?? 'Untitled',
          thumbnail_url: c.thumbnail_url ?? c.url,
          url: c.url ?? c.file_url ?? '',
          ai_tool: c.ai_tool ?? c.tool ?? 'RunImage',
          likes: c.likes ?? 0,
          created_at: c.created_at,
          buzz_post_id: c.buzz_post_id,
        }))
        setAiCreations(items)
        setTabLoading(false)
      })
      .catch(() => setTabLoading(false))
  }, [profile?.id])

  // SEO — meta tags + JSON-LD inject
  useEffect(() => {
    if (!profile) return
    const displayName = profile.display_name || profile.username
    document.title = `${displayName} (@${profile.username}) - RunAI Portfolio`

    // OG meta tags
    const setMeta = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute('property', property)
        document.head.appendChild(el)
      }
      el.content = content
    }
    setMeta('og:title', `${displayName} - RunAI Portfolio`)
    setMeta('og:description', profile.bio ?? `${displayName}'s AI creations on RunAI`)
    setMeta('og:image', profile.avatar_url ?? '')
    setMeta('og:url', `${window.location.origin}/u/${profile.username}`)
    setMeta('og:type', 'profile')

    // JSON-LD structured data
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: displayName,
      url: `${window.location.origin}/u/${profile.username}`,
      image: profile.avatar_url,
      description: profile.bio,
      sameAs: profile.social_accounts?.map(a => a.url) ?? [],
    }
    let scriptEl = document.querySelector('#profile-jsonld') as HTMLScriptElement
    if (!scriptEl) {
      scriptEl = document.createElement('script')
      scriptEl.id = 'profile-jsonld'
      scriptEl.type = 'application/ld+json'
      document.head.appendChild(scriptEl)
    }
    scriptEl.textContent = JSON.stringify(jsonLd)

    return () => {
      document.title = 'RunAI'
      scriptEl?.remove()
    }
  }, [profile])

  // Tab degistiginde veri yukle
  useEffect(() => {
    if (!profile) return
    switch (activeTab) {
      case 'gallery': loadGallery(); break
      case 'web': loadWeb(); break
      case 'videos': loadVideos(); break
      case 'lipsync': loadLipSync(); break
      case 'stats': loadStats(); break
      case 'ai-creations': loadAICreations(); break
      case 'buzz': break // ProfileBuzzPosts kendi verisini yukler
    }
  }, [activeTab, profile, loadGallery, loadWeb, loadVideos, loadLipSync, loadStats, loadAICreations])

  // Theme CSS degiskenleri
  const themeVars: Record<string, Record<string, string>> = {
    dark: { '--pp-bg': '#0d1117', '--pp-card': '#161b22', '--pp-text': '#e6edf3', '--pp-muted': '#8b949e', '--pp-border': '#30363d' },
    light: { '--pp-bg': '#f8fafc', '--pp-card': '#ffffff', '--pp-text': '#1e293b', '--pp-muted': '#64748b', '--pp-border': '#e2e8f0' },
    'runai-purple': { '--pp-bg': '#0f0a1e', '--pp-card': '#1a1230', '--pp-text': '#e6edf3', '--pp-muted': '#a78bfa', '--pp-border': '#2d1f5e' },
  }
  const tv = themeVars[theme]

  // Loading state — skeleton
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--body-bg, #0d1117)' }}>
        <div style={{ width: '100%', maxWidth: 960, padding: '24px 16px' }}>
          <div style={{ height: 220, borderRadius: 20, background: 'rgba(255,255,255,0.03)', marginBottom: 20, animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ height: 48, borderRadius: 14, background: 'rgba(255,255,255,0.03)', marginBottom: 20, animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {[1, 2, 3].map(i => <div key={i} style={{ height: 200, borderRadius: 14, background: 'rgba(255,255,255,0.03)', animation: 'pulse 1.5s ease-in-out infinite' }} />)}
          </div>
        </div>
      </div>
    )
  }

  // Not found
  if (!profile) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--body-bg, #0d1117)' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary, #e6edf3)', marginBottom: 8 }}>Portfolio not found</h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary, #8b949e)' }}>@{username} does not exist or has no public portfolio.</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: tv['--pp-bg'],
      fontFamily: "'DM Sans', 'Inter', sans-serif",
      transition: 'background 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      {/* Pulse animation keyframe */}
      <style>{`@keyframes pulse{0%,100%{opacity:0.4}50%{opacity:0.8}}@keyframes spin-slow{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px 60px' }}>
        {/* Theme switcher — sag ust */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 6 }}>
          {(['dark', 'light', 'runai-purple'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTheme(t)}
              aria-label={`Theme: ${t}`}
              style={{
                width: 24, height: 24, borderRadius: '50%', border: theme === t ? '2px solid #667eea' : '2px solid transparent',
                cursor: 'pointer',
                background: t === 'dark' ? '#0d1117' : t === 'light' ? '#f8fafc' : '#1a1230',
                transition: 'border-color 0.2s',
              }}
            />
          ))}
        </div>

        {/* Hero */}
        <ProfileHero user={profile} isOwner={isOwner} onEdit={() => window.location.href = '/old_dashboard'} />

        {/* Follow system */}
        <div style={{ marginTop: 16 }}>
          <ProfileFollowSystem username={username!} isOwner={isOwner} />
        </div>

        {/* Tab bar */}
        <div style={{ marginTop: 24, marginBottom: 20 }}>
          <ProfileTabBar
            active={activeTab}
            onChange={setActiveTab}
            counts={{
              gallery: gallery.length || undefined,
              videos: videos.length || undefined,
            }}
          />
        </div>

        {/* Tab content — glassmorphism container */}
        <section style={{
          padding: 24, borderRadius: 16,
          background: theme === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
          border: `1px solid ${tv['--pp-border']}`,
          backdropFilter: 'blur(8px)',
          minHeight: 300,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          {activeTab === 'gallery' && <ProfileGallery items={gallery} loading={tabLoading} />}
          {activeTab === 'web' && <ProfileWeb items={webSites} loading={tabLoading} />}
          {activeTab === 'videos' && <ProfileVideos items={videos} loading={tabLoading} />}
          {activeTab === 'lipsync' && <ProfileLipSync items={lipsyncs} loading={tabLoading} />}
          {activeTab === 'stats' && <ProfileStats stats={stats} loading={tabLoading} />}
          {activeTab === 'ai-creations' && <ProfileAICreations items={aiCreations} loading={tabLoading} username={username} />}
          {activeTab === 'buzz' && <ProfileBuzzPosts username={username!} />}
        </section>

        {/* Footer — Powered by RunAI */}
        <footer style={{
          marginTop: 40, textAlign: 'center', padding: '20px 0',
          borderTop: `1px solid ${tv['--pp-border']}`,
        }}>
          <p style={{ fontSize: 13, color: tv['--pp-muted'], marginBottom: 8 }}>
            Powered by <span style={{ fontWeight: 700, color: '#667eea' }}>RunAI</span>
          </p>
          <a
            href="/"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 18px', borderRadius: 10,
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none',
              boxShadow: '0 4px 16px rgba(102,126,234,0.3)',
              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)' }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)' }}
          >
            Get yours
          </a>
        </footer>
      </div>
    </div>
  )
}
